import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'

import { TESTIDS } from '../../config/testids'

export interface AriaBreathingBulbProps {
  /** Current loudness, 0–1, read once per animation frame. Never a React state value. */
  getLevel: () => number
  /** Someone is talking: the bulb tracks their voice. Otherwise it breathes gently. */
  isSpeaking: boolean
  /** Nothing is connected: the bulb rests dark until a session exists. */
  isDormant?: boolean
  size?: number
}

const DEFAULT_SIZE_PX = 120
/** Beyond this the retina cost outweighs the sharpness on a soft radial glow. */
const MAX_PIXEL_RATIO = 1.75
const MILLISECONDS_PER_SECOND = 1000
const TAU = Math.PI * 2

// Glow geometry as fractions of the canvas, so the bulb scales to any size.
const GLOW_RADIUS_BASE = 0.3
const GLOW_RADIUS_PER_LEVEL = 0.2
/**
 * The glow has to reach zero before it runs out of canvas. The centre is only half a width from the
 * edge but the corners are 0.707 away, so a gradient any wider than this is still visibly lit where it
 * meets the sides while the corners stay dark — which paints the canvas's own square onto the panel
 * every time someone speaks up. Nothing is lost by capping it: past here it was drawing a rectangle,
 * not a glow.
 */
const GLOW_RADIUS_MAX = 0.5
const CORE_RADIUS = 0.045
const CORE_GROWTH_PER_LEVEL = 0.6
const CORE_SHADOW_BLUR_PX = 28
const STREAK_HALF_WIDTH = 0.45

const DUST_MOTE_COUNT = 14
const DUST_MOTE_RADIUS_PX = 1.1

/**
 * The bulb is painted on a canvas, so it cannot use theme tokens directly — these are the rgb triples
 * behind them, named here rather than repeated inline. Amber is the brand `sun`; the outer indigo has
 * no token because it only ever exists as the cold edge of this glow.
 */
const BULB_RGB = {
  filament: '255, 244, 220',
  amber: '255, 171, 64',
  indigo: '80, 95, 255',
  dust: '255, 220, 170',
  streak: '255, 245, 225'
} as const

/** Opacity of each gradient stop at full brightness. */
const GLOW_STOP_ALPHA = { filament: 0.95, amber: 0.5, indigo: 0.13 }
const GLOW_STOP_POSITION = { amber: 0.25, indigo: 0.6 }
const DUST_ALPHA = { base: 0.12, perLevel: 0.3 }
const STREAK_ALPHA_PER_LEVEL = 0.45

/** How fast the drawn level chases the real one. Higher is twitchier. */
const LEVEL_ATTACK_PER_SECOND = 9
/** A long frame (tab was backgrounded) must not make the bulb jump. */
const MAX_FRAME_SECONDS = 0.05

/**
 * A soft glow gains nothing from 60 fps, and each frame costs a full-canvas gradient plus a blurred
 * shadow. Capping it leaves the main thread free for the work that is actually time-critical: posting
 * microphone chunks onto the socket.
 */
const TARGET_FRAMES_PER_SECOND = 30
const MIN_FRAME_SECONDS = 1 / TARGET_FRAMES_PER_SECOND

// Idle is a slow breath built from two out-of-phase sines, so it never looks like a loop.
const IDLE_BASE_LEVEL = 0.07
const IDLE_SLOW_WAVE = { amplitude: 0.05, speed: 1.3 }
const IDLE_FAST_WAVE = { amplitude: 0.02, speed: 3.7 }

/** Filament flicker: barely visible, but it stops the glow reading as a CSS animation. */
const FLICKER_AMPLITUDE = 0.015
const FLICKER_SPEED = 30

type DustMote = { x: number; y: number; speed: number; phase: number }

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

function createDust(random: () => number): DustMote[] {
  return Array.from({ length: DUST_MOTE_COUNT }, () => ({
    x: random(),
    y: random(),
    speed: 0.02 + random() * 0.05,
    phase: random() * TAU
  }))
}

/**
 * Aria made visible: a warm bulb that breathes when idle and swells with whoever is talking.
 *
 * A spoken session is otherwise completely silent to look at — there is no way to tell a working
 * microphone from a dead one, or Aria thinking from Aria finished. Driven straight from the audio
 * analysers via `getLevel`, so nothing here goes through React state and the animation cannot add
 * render work to the thread that is capturing audio.
 */
export const AriaBreathingBulb = ({
  getLevel,
  isSpeaking,
  isDormant,
  size = DEFAULT_SIZE_PX
}: AriaBreathingBulbProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  // Read inside the loop so a prop change never restarts the animation.
  const stateRef = useRef({ getLevel, isSpeaking, isDormant })
  stateRef.current = { getLevel, isSpeaking, isDormant }

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return

    const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO)
    canvas.width = Math.round(size * pixelRatio)
    canvas.height = Math.round(size * pixelRatio)
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)

    const dust = createDust(Math.random)
    let drawnLevel = 0
    let frameHandle = 0
    let lastFrameSeconds = performance.now() / MILLISECONDS_PER_SECOND

    const drawGlow = (centre: number, radius: number, brightness: number) => {
      const gradient = context.createRadialGradient(centre, centre, 0, centre, centre, Math.max(1, radius))
      gradient.addColorStop(0, `rgba(${BULB_RGB.filament}, ${GLOW_STOP_ALPHA.filament * brightness})`)
      gradient.addColorStop(GLOW_STOP_POSITION.amber, `rgba(${BULB_RGB.amber}, ${GLOW_STOP_ALPHA.amber * brightness})`)
      gradient.addColorStop(
        GLOW_STOP_POSITION.indigo,
        `rgba(${BULB_RGB.indigo}, ${GLOW_STOP_ALPHA.indigo * brightness})`
      )
      gradient.addColorStop(1, `rgba(${BULB_RGB.indigo}, 0)`)
      context.fillStyle = gradient
      context.fillRect(0, 0, size, size)
    }

    const drawDust = (elapsedSeconds: number, deltaSeconds: number, level: number) => {
      for (const mote of dust) {
        mote.y -= mote.speed * deltaSeconds * 2
        if (mote.y < 0) {
          mote.y = 1
          mote.x = Math.random()
        }
        const twinkle = 0.5 + 0.5 * Math.sin(elapsedSeconds * 2 + mote.phase)
        context.fillStyle = `rgba(${BULB_RGB.dust}, ${(DUST_ALPHA.base + level * DUST_ALPHA.perLevel) * twinkle})`
        context.beginPath()
        context.arc(mote.x * size, mote.y * size, DUST_MOTE_RADIUS_PX, 0, TAU)
        context.fill()
      }
    }

    const drawFrame = (elapsedSeconds: number, deltaSeconds: number) => {
      const { getLevel: readLevel, isSpeaking: speaking, isDormant: dormant } = stateRef.current
      const idleLevel =
        IDLE_BASE_LEVEL +
        IDLE_SLOW_WAVE.amplitude * Math.sin(elapsedSeconds * IDLE_SLOW_WAVE.speed) +
        IDLE_FAST_WAVE.amplitude * Math.sin(elapsedSeconds * IDLE_FAST_WAVE.speed)
      const target = speaking ? clamp01(readLevel()) : idleLevel

      drawnLevel += (target - drawnLevel) * clamp01(deltaSeconds * LEVEL_ATTACK_PER_SECOND)
      const level = dormant ? 0 : clamp01(drawnLevel)

      const centre = size / 2
      const brightness = 0.35 + level * 0.65
      const flicker = 1 + Math.sin(elapsedSeconds * FLICKER_SPEED) * FLICKER_AMPLITUDE * level

      const glowRadius = Math.min(
        size * (GLOW_RADIUS_BASE + level * GLOW_RADIUS_PER_LEVEL) * flicker,
        size * GLOW_RADIUS_MAX
      )

      context.clearRect(0, 0, size, size)
      drawGlow(centre, glowRadius, brightness)
      drawDust(elapsedSeconds, deltaSeconds, level)

      context.fillStyle = '#fff'
      context.shadowColor = `rgba(${BULB_RGB.amber}, 1)`
      context.shadowBlur = CORE_SHADOW_BLUR_PX
      context.beginPath()
      context.arc(centre, centre, size * CORE_RADIUS * (1 + level * CORE_GROWTH_PER_LEVEL) * flicker, 0, TAU)
      context.fill()
      context.shadowBlur = 0

      const streak = context.createLinearGradient(
        centre - size * STREAK_HALF_WIDTH,
        0,
        centre + size * STREAK_HALF_WIDTH,
        0
      )
      streak.addColorStop(0, 'transparent')
      streak.addColorStop(0.5, `rgba(${BULB_RGB.streak}, ${level * STREAK_ALPHA_PER_LEVEL})`)
      streak.addColorStop(1, 'transparent')
      context.fillStyle = streak
      context.fillRect(centre - size * STREAK_HALF_WIDTH, centre - 1, size * STREAK_HALF_WIDTH * 2, 2)
    }

    // Someone who asked for less motion gets the bulb, not the breathing.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      drawFrame(0, MAX_FRAME_SECONDS)
      return
    }

    const renderLoop = (now: number) => {
      frameHandle = requestAnimationFrame(renderLoop)

      const nowSeconds = now / MILLISECONDS_PER_SECOND
      const sinceLastFrame = nowSeconds - lastFrameSeconds
      if (sinceLastFrame < MIN_FRAME_SECONDS) return

      lastFrameSeconds = nowSeconds
      drawFrame(nowSeconds, Math.min(MAX_FRAME_SECONDS, Math.max(0, sinceLastFrame)))
    }
    frameHandle = requestAnimationFrame(renderLoop)

    return () => cancelAnimationFrame(frameHandle)
  }, [size])

  return (
    <Box
      aria-hidden
      component="canvas"
      ref={canvasRef}
      data-testid={TESTIDS.ARIA_BREATHING_BULB}
      sx={{ width: size, height: size, display: 'block', flexShrink: 0 }}
    />
  )
}

export default AriaBreathingBulb
